/** Role helpers for V2 shell navigation and access gates.

 * Capabilities mirror backend `src/authz/capabilities.py`.
 * Viewers keep Ask + all agent read tools; contribute caps require Editor+.
 */

export const CAP_MAP_READ = "map.read";
export const CAP_ASK_READ = "ask.read";
export const CAP_AGENT_READ_TOOLS = "agent.read_tools";
export const CAP_ASK_SPEND = "ask.spend";
export const CAP_MAP_WRITE = "map.write";
export const CAP_KNOWLEDGE_WRITE = "knowledge.write";
export const CAP_AI_ENRICH = "ai.enrich";
export const CAP_AGENT_WRITE_TOOLS = "agent.write_tools";
export const CAP_ADMIN_ORG = "admin.org";

const VIEWER_CAPS = new Set([
  CAP_MAP_READ,
  CAP_ASK_READ,
  CAP_AGENT_READ_TOOLS,
  CAP_ASK_SPEND,
]);

const EDITOR_CAPS = new Set([
  ...VIEWER_CAPS,
  CAP_MAP_WRITE,
  CAP_KNOWLEDGE_WRITE,
  CAP_AI_ENRICH,
  CAP_AGENT_WRITE_TOOLS,
]);

const ADMIN_CAPS = new Set([...EDITOR_CAPS, CAP_ADMIN_ORG]);

const ROLE_RANK = { Viewer: 1, Editor: 2, Administrator: 3 };

/** Canonical role label used by API + capability maps. */
export function normalizeUserRole(role) {
  const raw = String(role || "").trim();
  if (!raw) return "";
  const key = raw.toLowerCase().replace(/[^a-z]/g, "");
  if (key.includes("administrator") || key === "admin") return "Administrator";
  if (key.includes("editor") || key.includes("contributor")) return "Editor";
  if (key.includes("viewer") || key === "readonly") return "Viewer";
  return raw;
}

function roleFromJwt() {
  try {
    const token = localStorage.getItem("access_token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return normalizeUserRole(payload?.role);
  } catch {
    return "";
  }
}

/**
 * Resolve effective role from stored user + JWT.
 * Takes the higher privilege so a DB/role upgrade reflected in /me is not
 * blocked by an older Viewer token, and a fresh Admin JWT is not blocked by
 * a stale Viewer user blob.
 */
export function resolveUserRole(user = readStoredUser()) {
  const fromUser = normalizeUserRole(user?.role);
  const fromJwt = roleFromJwt();
  const a = ROLE_RANK[fromUser] || 0;
  const b = ROLE_RANK[fromJwt] || 0;
  if (a >= b && fromUser) return fromUser;
  if (fromJwt) return fromJwt;
  return fromUser;
}

export function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function userIsAdministrator(user = readStoredUser()) {
  if (!user && !roleFromJwt()) return false;
  if (user?.is_super_user) return true;
  return resolveUserRole(user) === "Administrator";
}

export function currentUserId(user = readStoredUser()) {
  if (!user) return "";
  return String(user.id || user.user_id || "").trim();
}

/** Read-only workspace role — no inbox, add knowledge, or approvals. */
export function userIsViewer(user = readStoredUser()) {
  if (!user && !roleFromJwt()) return true;
  if (user?.is_super_user) return false;
  return resolveUserRole(user) === "Viewer";
}

export function capabilitiesForUser(user = readStoredUser()) {
  if (!user && !roleFromJwt()) return new Set(VIEWER_CAPS);
  if (user?.is_super_user) return new Set(ADMIN_CAPS);
  const role = resolveUserRole(user);
  if (role === "Administrator") return new Set(ADMIN_CAPS);
  if (role === "Editor") return new Set(EDITOR_CAPS);
  return new Set(VIEWER_CAPS);
}

export function userHasCapability(capability, user = readStoredUser()) {
  return capabilitiesForUser(user).has(capability);
}

/** May upload knowledge, use inbox, and submit items for approval. */
export function userCanContribute(user = readStoredUser()) {
  const caps = capabilitiesForUser(user);
  return caps.has(CAP_MAP_WRITE) || caps.has(CAP_KNOWLEDGE_WRITE);
}

/** Viewers and above may use Ask with the full read-only agent tool set. */
export function userCanAsk(user = readStoredUser()) {
  return userHasCapability(CAP_ASK_READ, user);
}
